import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DxGalleryModule } from 'devextreme-angular';
import { MatDialogModule } from '@angular/material/dialog';
import { AbcCoreModule, MimeTypeEnum } from '@barthauer/abc/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import {
  MediaDocument,
  DataChangedEventArgs,
  DataChangedType,
  GalleryImage,
  GalleryImageType,
  SelectedMedia,
} from './models/abc-media-gallery.model';
import { SourceSelectionComponent } from './source-selection/source-selection.component';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ItemClickEvent } from 'devextreme/ui/gallery';

@Component({
  selector: 'abc-media-gallery',
  standalone: true,
  imports: [
    MatIconModule,
    DxGalleryModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    AbcCoreModule
],
  templateUrl: './abc-media-gallery.component.html',
  styleUrls: ['./abc-media-gallery.component.scss'],
})
export class AbcMediaGalleryComponent implements OnInit {
  @Input() enabled: boolean = true;
  @Output() onDataChanged = new EventEmitter<DataChangedEventArgs>();
  private _dataSource: MediaDocument[] = [];
  public internalDataSource: GalleryImage[] = [];
  public internalSelectedIndex: number = 0;
  private resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  @Input()
  get dataSource(): MediaDocument[] {
    return this._dataSource;
  }
  set dataSource(documents: MediaDocument[]) {
    this._dataSource = documents;
    this.updateSourceFromMediaDocuments();
  }

  @Input()
  get selectedIndex() {
    return this.internalSelectedIndex;
  }
  set selectedIndex(i: number) {
    this.internalSelectedIndex = i;
  }

  private get selectedGalleryImage(): GalleryImage {
    if (this.internalDataSource.length > 0) {
      return this.internalDataSource[this.internalSelectedIndex];
    }
    return null;
  }

  get selectedMediaDocument(): MediaDocument {
    if (this.selectedGalleryImage?.type === GalleryImageType.Regular) {
      return this.selectedGalleryImage.sourceDocument;
    }
    return null;
  }

  constructor(
    private abcContainer: AbcContainerService,
    private resourceManagerService: ResourceManagerService,
  ) {
    this.resAsync = this.resourceManagerService.getResourceSet('abc-media-gallery');
    this.dataSource = new Array<MediaDocument>(); // in case no datasource will be set -> "adding mode" only
  }
  async ngOnInit() {
    this.res = await this.resAsync;
  }
  private async updateSourceFromMediaDocuments(): Promise<void> {
    this.res = await this.resAsync; //calles ngOnInit also

    const newSource: Array<GalleryImage> = this._dataSource.map<GalleryImage>((doc) => {
      const img = new GalleryImage();
      img.imageSrc = doc.previewImageDataUrl.toString();
      img.sourceDocument = doc;
      return img;
    });

    const imgAddNewPlaceholder = new GalleryImage();
    imgAddNewPlaceholder.type = GalleryImageType.AddNewPlaceholder;
    imgAddNewPlaceholder.imageSrc = this.res.svgUrl('camera-placeholder');
    newSource.push(imgAddNewPlaceholder);

    this.internalDataSource = [...newSource];

    this.selectedIndex = this.internalDataSource.length - 1;
  }

  public itemClick(e: ItemClickEvent) {
    if ((e.itemData as GalleryImage).type === GalleryImageType.AddNewPlaceholder) {
      const target = e.event?.target as HTMLElement;

      if (target.tagName.toLowerCase() !== 'img') {
        return;
      }

      const img = target as HTMLImageElement;
      const bounds = this.getRenderedImageBounds(img);
      const nativeEvent = e.event as unknown as MouseEvent;

      const clickedOnImage =
        nativeEvent.clientX >= bounds.left &&
        nativeEvent.clientX <= bounds.right &&
        nativeEvent.clientY >= bounds.top &&
        nativeEvent.clientY <= bounds.bottom;

      if (clickedOnImage) {
        this.openSourceSelectionWindow();
      }
    }
  }

  private getRenderedImageBounds(img: HTMLImageElement): {
    left: number;
    right: number;
    top: number;
    bottom: number;
  } {
    const rect = img.getBoundingClientRect();
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    const displayW = rect.width;
    const displayH = rect.height;

    const naturalRatio = naturalW / naturalH;
    const displayRatio = displayW / displayH;

    let renderedW: number;
    let renderedH: number;

    if (naturalRatio > displayRatio) {
      renderedW = displayW;
      renderedH = displayW / naturalRatio;
    } else {
      renderedH = displayH;
      renderedW = displayH * naturalRatio;
    }

    const offsetX = (displayW - renderedW) / 2;
    const offsetY = (displayH - renderedH) / 2;

    return {
      left: rect.left + offsetX,
      right: rect.left + offsetX + renderedW,
      top: rect.top + offsetY,
      bottom: rect.top + offsetY + renderedH,
    };
  }

  private openSourceSelectionWindow(): void {
    const style: WindowStyle = {
      height: '165px',
      width: '380px',
      shading: true,
      resizable: false,
      position: { my: 'center center', at: 'center center', offset: '0 0' },
    };

    const WindowAttr: IWindow = {
      id: 'sourceSelectionWindow',
      label: this.res?.get('SourceSelection_Title'),
      maximizeButton: false,
      minimizeButton: false,
      component: SourceSelectionComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    const window = this.abcContainer.createComponent(abcWindow, AbcWindowComponent, WindowAttr);
    const windowInstance: AbcWindowComponent = <AbcWindowComponent>window.componentRef.instance;
    windowInstance.createComponentInstance().subscribe(async (windowComponent) => {
      const sourceSelectionInstance: SourceSelectionComponent = <SourceSelectionComponent>(
        windowComponent.openWindow().instance
      );

      sourceSelectionInstance.onMediaSelected.subscribe((media: SelectedMedia[]) => this.addNewDocuments(media));

      sourceSelectionInstance.onCloseDemand.subscribe(() => windowInstance.closeDialog());
      windowComponent.onAfterClosed.subscribe(() => window.componentRef.destroy());
    });
  }

  private addNewDocuments(medias: SelectedMedia[]): void {
    const newMediaDocs: MediaDocument[] = new Array<MediaDocument>();
    for (const media of medias) {
      const newMediaDoc = new MediaDocument();
      const newGalleryImg = new GalleryImage();
      newGalleryImg.sourceDocument = newMediaDoc;
      if (media.dataUrl) {
        newMediaDoc.previewImageDataUrl = media.dataUrl;
      } else {
        newMediaDoc.designation = media.file.name;
        newMediaDoc.file = media.file;
      }
      newGalleryImg.imageSrc = newMediaDoc.previewImageDataUrl.toString();
      newMediaDocs.push(newMediaDoc);
      this.dataSource.push(newMediaDoc);
      //insert right before camera placeholder
      this.internalDataSource.splice(this.internalDataSource.length - 1, 0, newGalleryImg);
    }
    this.onDataChanged.emit(
      new DataChangedEventArgs({
        documents: newMediaDocs,
        type: DataChangedType.Added,
      }),
    );
    setTimeout(() => {
      this.selectedIndex = this.internalDataSource.length - 2;
    }, 0);
  }

  public static drawTextImage(
    text: string,
    x: number = 0,
    y: number = 0,
    width: number = 200,
    height: number = 150,
  ): string {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#DEDEDE';
    ctx.fillRect(x + 1, y + 1, width - 2, height - 2);
    ctx.strokeStyle = '#777777';
    ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.fillText(text, x + width / 2.0, y + height / 2.0, width * 0.8);

    return canvas.toDataURL(MimeTypeEnum.PNG);
  }

  public async downloadPreview(): Promise<void> {
    const selectedGalleryImage = this.selectedGalleryImage;
    const selectedMediaDoc = selectedGalleryImage.sourceDocument;
    if (selectedMediaDoc?.downloadable) {
      const dataUrl = await selectedMediaDoc.onDownloadButtonClick();
      selectedMediaDoc.previewImageDataUrl = dataUrl;
      selectedGalleryImage.imageSrc = dataUrl.toString();
      selectedMediaDoc.downloadable = false;
    }
  }
}
